import { Link } from 'react-router-dom';
import { Linkedin, Twitter, Facebook, Instagram, Mail, ArrowRight } from 'lucide-react';
import { Input } from './ui/input';
import { Button } from './ui/button';
import { useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Footer = () => {
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);

  const handleNewsletterSubmit = async (e) => {
    e.preventDefault();
    if (!email) return;

    setLoading(true);
    try {
      await axios.post(`${API}/newsletter/subscribe`, { email });
      toast.success('Successfully subscribed to the newsletter!');
      setEmail('');
    } catch (error) {
      toast.error('Failed to subscribe. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const currentYear = new Date().getFullYear();

  const footerLinks = {
    explore: [
      { name: 'About', href: '/#about' },
      { name: 'Services', href: '/#services' },
      { name: 'Portfolio', href: '/#portfolio' },
      { name: 'Blog', href: '/blog' },
    ],
    topics: [
      { name: 'Emerging Topics', href: '/blog?category=Emerging Topics' },
      { name: 'Finance & Fintech', href: '/blog?category=Fintech' },
      { name: 'Regulatory Insights', href: '/blog?category=Regulatory' },
      { name: 'Global Economy', href: '/blog?category=Finance' },
    ],
    connect: [
      { name: 'Contact', href: '/#contact' },
      { name: 'LinkedIn', href: 'https://linkedin.com/in/tanweerquadri', external: true },
      { name: 'Download CV', href: '/cv-placeholder.pdf' },
    ],
  };

  const socialLinks = [
    { icon: Linkedin, href: 'https://linkedin.com/in/tanweerquadri', label: 'LinkedIn' },
    { icon: Twitter, href: 'https://twitter.com/tanweerquadri', label: 'X (Twitter)' },
    { icon: Facebook, href: 'https://facebook.com/tanweerquadri', label: 'Facebook' },
    { icon: Instagram, href: 'https://instagram.com/tanweerquadri', label: 'Instagram' },
  ];

  return (
    <footer className="bg-primary text-white" data-testid="main-footer">
      <div className="container-padding py-16 md:py-24">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-12 md:gap-8">
          {/* Brand & Newsletter */}
          <div className="md:col-span-5">
            <Link to="/" className="inline-block mb-6">
              <img 
                src="https://customer-assets.emergentagent.com/job_d9ecf978-8ae5-49a9-be32-11498e2498af/artifacts/5tt0fjyc_Gemini_Generated_Image_pd4qetpd4qetpd4qq.png" 
                alt="TQ Finance Insights" 
                className="h-14 w-auto brightness-0 invert"
              />
            </Link>
            <p className="text-white/70 font-serif leading-relaxed mb-8 max-w-md">
              Cutting-edge insights on accounting, finance, regulation, and the forces reshaping the global economic landscape.
            </p>
            
            {/* Newsletter Signup */}
            <div className="mb-8">
              <p className="label-uppercase text-white/50 mb-4">Subscribe to Updates</p>
              <form onSubmit={handleNewsletterSubmit} className="flex gap-2">
                <Input
                  type="email"
                  placeholder="Enter your email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="bg-white/10 border-white/20 text-white placeholder:text-white/50 rounded-none flex-1"
                  data-testid="footer-newsletter-input"
                />
                <Button 
                  type="submit" 
                  disabled={loading}
                  className="bg-accent hover:bg-accent/90 rounded-none px-4"
                  data-testid="footer-newsletter-submit"
                >
                  <ArrowRight className="w-5 h-5" />
                </Button>
              </form>
            </div>

            {/* Social Links */}
            <div className="flex gap-4">
              {socialLinks.map((social) => (
                <a
                  key={social.label}
                  href={social.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-10 h-10 border border-white/20 flex items-center justify-center hover:bg-white/10 transition-colors"
                  aria-label={social.label}
                  data-testid={`social-${social.label.toLowerCase().replace(/[^a-z]/g, '')}`}
                >
                  <social.icon className="w-5 h-5" />
                </a>
              ))}
            </div>
          </div>

          {/* Links */}
          <div className="md:col-span-7">
            <div className="grid grid-cols-2 md:grid-cols-3 gap-8">
              <div>
                <p className="label-uppercase text-white/50 mb-6">Explore</p>
                <ul className="space-y-3">
                  {footerLinks.explore.map((link) => (
                    <li key={link.name}>
                      <Link 
                        to={link.href} 
                        className="text-white/70 hover:text-white transition-colors font-sans text-sm"
                      >
                        {link.name}
                      </Link>
                    </li>
                  ))}
                </ul>
              </div>
              <div>
                <p className="label-uppercase text-white/50 mb-6">Topics</p>
                <ul className="space-y-3">
                  {footerLinks.topics.map((link) => (
                    <li key={link.name}>
                      <Link 
                        to={link.href} 
                        className="text-white/70 hover:text-white transition-colors font-sans text-sm"
                      >
                        {link.name}
                      </Link>
                    </li>
                  ))}
                </ul>
              </div>
              <div>
                <p className="label-uppercase text-white/50 mb-6">Connect</p>
                <ul className="space-y-3">
                  {footerLinks.connect.map((link) => (
                    <li key={link.name}>
                      {link.external ? (
                        <a 
                          href={link.href}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-white/70 hover:text-white transition-colors font-sans text-sm"
                        >
                          {link.name}
                        </a>
                      ) : (
                        <Link 
                          to={link.href} 
                          className="text-white/70 hover:text-white transition-colors font-sans text-sm"
                        >
                          {link.name}
                        </Link>
                      )}
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        </div>

        {/* Bottom Bar */}
        <div className="border-t border-white/10 mt-16 pt-8 flex flex-col md:flex-row justify-between items-center gap-4">
          <p className="text-white/50 text-sm font-sans">
            © {currentYear} Tanweer Quadri. All rights reserved.
          </p>
          <div className="flex gap-6">
            <Link to="/privacy" className="text-white/50 hover:text-white text-sm font-sans transition-colors">
              Privacy Policy
            </Link>
            <Link to="/terms" className="text-white/50 hover:text-white text-sm font-sans transition-colors">
              Terms of Use
            </Link>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
